import { useEffect, useState, useMemo } from "react";
import { useApp } from "../context/AppContext";
import { useLocation, useNavigate } from "react-router-dom";
import MentionMappingRow from "../components/MentionMappingRow";
import PromptLibraryModal from "../components/PromptLibraryModal";
import AddServerMentionMappingModal from "../components/AddServerMentionMappingModal";
import { BOT_INVITE_URL } from "../config";
const API_BASE = import.meta.env.VITE_API_URL || "";

export default function SettingsPage() {
  const {
    user,
    models,
    setModels,
    fetchModels,
    defaultBehavior,
    setDefaultBehavior,
    globalPrompt,
    setGlobalPrompt,
    saveGlobalPrompt,
    savingGlobalPrompt,
    allowOthers,
    saveAllowOthersPreference,
    showToast,
    trackEvent
  } = useApp();

  const location = useLocation();
  const navigate = useNavigate();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null);
  const [billingStatusLoading, setBillingStatusLoading] = useState(true);
  const [showPromptLibrary, setShowPromptLibrary] = useState(false);
  const [contextLimit, setContextLimit] = useState(60);
  const [savingLimit, setSavingLimit] = useState(false);
  const [defaultModelLoading, setDefaultModelLoading] = useState(false);
  const [modelSelectorOpen, setModelSelectorOpen] = useState(false);
  const [modelSelectorSearch, setModelSelectorSearch] = useState("");

  // Server @mentions (server-bound mention model)
  const [showAddMapping, setShowAddMapping] = useState(false);
  const [guilds, setGuilds] = useState(null);
  const [assignments, setAssignments] = useState(null);
  const [loadingGuilds, setLoadingGuilds] = useState(false);

  // Memory settings
  const [memorySettings, setMemorySettings] = useState(null);
  const [loadingMemory, setLoadingMemory] = useState(false);
  const [savingMemory, setSavingMemory] = useState(false);
  const [creatingChannel, setCreatingChannel] = useState(false);
  const [needsPermissionUpdate, setNeedsPermissionUpdate] = useState(false);

  // Custom Dropdown States for Memory Settings
  const [serverSelectorOpen, setServerSelectorOpen] = useState(false);
  const [serverSelectorSearch, setServerSelectorSearch] = useState("");
  const [extractorSelectorOpen, setExtractorSelectorOpen] = useState(false);
  const [extractorSelectorSearch, setExtractorSelectorSearch] = useState("");

  function fetchGuildsAndAssignments(silent = false) {
    setLoadingGuilds(true);
    fetch(`${API_BASE}/api/servers`, { credentials: "include" })
      .then((r) => {
        if (r.status === 401) throw new Error("Discord session expired. Re-login required.");
        if (!r.ok) throw new Error(`Discord API Error: ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (data.error) throw new Error(data.error);
        setGuilds(data.guilds || []);
        setAssignments(data.assignments || {});
      })
      .catch((err) => {
        if (!silent) showToast(err.message, "error");
      })
      .finally(() => setLoadingGuilds(false));
  }

  function openAddMapping() {
    setShowAddMapping(true);
    if (!guilds) fetchGuildsAndAssignments();
  }

  // Summary for Settings card
  const assignedGuildIds = assignments ? Object.keys(assignments) : [];
  const assignedGuilds = (guilds || []).filter(g => assignments && assignments[g.id]);
  const assignedCount = assignedGuildIds.length;

  function handleDeleteAccount() {
    if (deleteConfirmText !== "DELETE") {
      showToast("Please type DELETE to confirm", "error");
      return;
    }

    setDeleting(true);
    trackEvent("delete_account_start", {});
    fetch(`${API_BASE}/api/user/delete`, {
      method: "DELETE",
      credentials: "include",
    })
      .then(async r => {
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          throw new Error(data.error || "Failed to delete account");
        }
        showToast("Account deleted successfully", "success");
        trackEvent("delete_account_success", {});
        setTimeout(() => {
          window.location.href = "/";
        }, 1500);
      })
      .catch(err => {
        showToast(err.message, "error");
        setDeleting(false);
      });
  }

  // Locate the currently pinned default model (if any)
  const activeDefaultModel = useMemo(() => {
    if (!models) return null;
    for (const group of models) {
      const found = group.models?.find(m => m.isDefault);
      if (found) return found;
    }
    return null;
  }, [models]);

  async function handleDefaultDropdownChange(e) {
    const val = e.target.value;


    // Optimistically update the UI instantly
    if (val === "last") {
      setDefaultBehavior("last");
    } else {
      setDefaultBehavior("default");
      if (models) {
        setModels(models.map(group => ({
          ...group,
          models: group.models.map(m => ({
            ...m,
            isDefault: m.id.toString() === val.toString()
          }))
        })));
      }
    }

    setDefaultModelLoading(true);
    try {
      if (val === "last") {
        const res = await fetch(`${API_BASE}/api/preferences/default-behavior`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ behavior: "last" })
        });
        if (!res.ok) throw new Error("Failed to set dynamic preference");
        showToast("Set to Last Used Model (Dynamic)", "success");
        trackEvent("model_behavior_set", { type: "last" });
      } else {
        const res = await fetch(`${API_BASE}/api/models/${val}/set-default`, {
          method: "POST",
          credentials: "include"
        });
        if (!res.ok) throw new Error("Failed to pin model");
        await fetchModels(); // refreshes isDefault flags in context
        showToast("Default model pinned", "success");
        trackEvent("model_behavior_set", { type: "explicit" });
      }
    } catch (err) {
      showToast(err.message, "error");
      fetchModels(); // Revert to source of truth if it failed
    } finally {
      setDefaultModelLoading(false);
    }
  }

  const flatModels = useMemo(() => {
    const groups = Array.isArray(models) ? models : [];
    return groups.flatMap(pg =>
      (pg.models || []).map(m => ({ ...m, provider: pg.provider }))
    );
  }, [models]);

  const extractionModels = useMemo(() => (
    flatModels.filter(m => m.enabled && m.supportsChat === true && !m.isCustom)
  ), [flatModels]);

  const activeExtractorModel = useMemo(() => {
    if (!memorySettings || !flatModels.length) return null;
    return flatModels.find(m => m.provider === memorySettings.extractor_provider && m.name === memorySettings.extractor_model);
  }, [memorySettings, flatModels]);

  const activeGuild = useMemo(() => {
    if (!memorySettings || !guilds) return null;
    return guilds.find(g => g.id === memorySettings.guild_id);
  }, [memorySettings, guilds]);

  const activeExtractorLabel = activeExtractorModel?.displayName || activeExtractorModel?.name || "";
  const autoExtractorLabel = activeExtractorLabel ? `Auto: ${activeExtractorLabel}` : "Auto";
  const extractorInputValue = extractorSelectorOpen
    ? extractorSelectorSearch
    : (memorySettings?.extractor_auto ? autoExtractorLabel : (activeExtractorLabel || "Pick a model"));

  function stripMemoryTransientFields(settings) {
    const persistedSettings = { ...settings };
    delete persistedSettings.warning;
    if (persistedSettings.extractor_auto) {
      persistedSettings.extractor_provider = "";
      persistedSettings.extractor_model = "";
    }
    delete persistedSettings.extractor_auto;
    return persistedSettings;
  }

  function fetchMemorySettings() {
    return fetch(`${API_BASE}/api/memory/settings`, { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        if (data.error) throw new Error(data.error);
        setMemorySettings(data);
        return data;
      });
  }

  useEffect(() => {
    fetch(`${API_BASE}/api/preferences/context-limit`, { credentials: "include" })
      .then(r => r.json())
      .then(data => setContextLimit(data.context_limit_percent));

    setLoadingMemory(true);
    fetchMemorySettings()
      .catch(() => undefined)
      .finally(() => setLoadingMemory(false));
  }, []);

  function saveContextLimit(val) {
    setContextLimit(val); // optimistic update
    setSavingLimit(true);
    fetch(`${API_BASE}/api/preferences/context-limit`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ context_limit_percent: parseInt(val) }),
    }).finally(() => setSavingLimit(false));
  }

  function saveMemorySettings(newSettings) {
    const settingsToSave = stripMemoryTransientFields(newSettings);
    setSavingMemory(true);
    setMemorySettings({ ...newSettings, warning: undefined }); // optimistic update
    fetch(`${API_BASE}/api/memory/settings`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(settingsToSave),
    })
      .then(r => r.json())
      .then(data => {
        if (data.error) throw new Error(data.error);
        showToast("Memory settings saved", "success");
        return fetchMemorySettings();
      })
      .catch(err => {
        showToast(err.message, "error");
        // Re-fetch on error to revert optimistic update
        fetchMemorySettings().catch(() => undefined);
      })
      .finally(() => setSavingMemory(false));
  }

  function handleCreateMemoryChannel() {
    if (!memorySettings.guild_id) {
      showToast("Please select a home server first", "error");
      return;
    }
    setCreatingChannel(true);
    fetch(`${API_BASE}/api/memory/create-channel`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ guild_id: memorySettings.guild_id }),
    })
      .then(async r => {
        const text = await r.text();
        let data = {};
        try {
          data = text ? JSON.parse(text) : {};
        } catch {
          data = {};
        }

        if (!r.ok || data.error) {
          const error = new Error(data.error || `Server error: ${r.status}`);
          error.code = data.code || "";
          throw error;
        }
        return data;
      })
      .then(data => {
        // Automatically save the new channel ID in settings
        saveMemorySettings({ ...memorySettings, channel_id: data.channel_id, enabled: true });
        showToast("Memory channel ready!", "success");
      })
      .catch(err => {
        if (err.code === "discord_bot_not_in_guild") {
          setNeedsPermissionUpdate("invite");
        } else if (err.code === "discord_missing_permissions") {
          setNeedsPermissionUpdate("permissions");
        } else {
          setNeedsPermissionUpdate(false);
          showToast(err.message, "error");
        }
      })
      .finally(() => setCreatingChannel(false));
  }

  function handleInsertPrompt(text) {
    setGlobalPrompt(prev => {
      const trimmed = (prev || "").trim();
      return trimmed ? trimmed + "\n\n" + text : text;
    });
    setShowPromptLibrary(false);
    showToast("Prompt appended!", "success");
  }


  async function startCheckout(plan) {
    if (billingLoading) return;
    trackEvent("upgrade_checkout_start", { plan, source: "settings" });
    setBillingLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/billing/checkout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) {
        throw new Error(data?.error || "Failed to start checkout");
      }
      window.location = data.url;
    } catch (e) {
      showToast(e.message || "Failed to start checkout", "error");
    } finally {
      setBillingLoading(false);
    }
  }

  async function openBillingPortal() {
    if (billingLoading) return;
    setBillingLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/billing/portal`, {
        method: "POST",
        credentials: "include"
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) {
        throw new Error(data?.error || "Failed to open billing portal");
      }
      window.location = data.url;
    } catch (e) {
      showToast(e.message || "Failed to open billing portal", "error");
    } finally {
      setBillingLoading(false);
    }
  }

  // Load current subscription status
  useEffect(() => {
    let mounted = true;
    setBillingStatusLoading(true);
    fetch(`${API_BASE}/api/billing/status`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (mounted) setBillingStatus(data); })
      .finally(() => { if (mounted) setBillingStatusLoading(false); });
    return () => { mounted = false; };
  }, []);

  // Load server @mentions mappings when Settings opens (so no manual Refresh needed)
  useEffect(() => {
    if (!guilds && !loadingGuilds) {
      fetchGuildsAndAssignments(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Checkout return query param (?billing=success|cancelled)
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const billing = qs.get("billing");
    if (billing === "success") {
      // Refresh status after a successful checkout redirect
      fetch(`${API_BASE}/api/billing/status`, { credentials: "include" })
        .then(r => r.ok ? r.json() : null)
        .then(data => setBillingStatus(data))
        .finally(() => { });
      // Show a one-time toast and tidy URL
      showToast("Payment successful — you are now Premium!", "success");
      const url = new URL(window.location.href);
      url.searchParams.delete("billing");
      navigate(url.pathname, { replace: true });
    } else if (billing === "cancelled") {
      showToast("Checkout cancelled", "info");
      const url = new URL(window.location.href);
      url.searchParams.delete("billing");
      navigate(url.pathname, { replace: true });
    }
  }, [location.search, showToast, navigate]);

  // Auto-start checkout if ?startCheckout=plan param exists
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const checkoutPlan = qs.get("startCheckout");

    if (checkoutPlan && !billingLoading && !billingStatusLoading) {
      // Remove the param first to avoid loops
      const url = new URL(window.location.href);
      url.searchParams.delete("startCheckout");
      navigate(url.pathname, { replace: true });

      // Start checkout
      startCheckout(checkoutPlan);
    }
  }, [location.search, billingLoading, billingStatusLoading, navigate]);

  return (
    <>
      <div className="card">
        <div className="card-header">
          <svg className="card-icon" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
            <path d="M7 9h10v2H7zm0-3h10v2H7zm0 6h7v2H7z" />
          </svg>
          <h2>Global System Prompt</h2>
        </div>
        <div style={{ marginTop: "1rem" }}>
          <div className="settings-info-box">
            <div className="settings-info-row">
              <svg width={20} height={20} fill="var(--accent)" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: "2px" }}>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
              </svg>
              <div style={{ fontSize: "0.875rem", color: "var(--text-muted)", lineHeight: "1.5" }}>
                <strong style={{ color: "var(--text-secondary)" }}>What is this?</strong> This prompt will be automatically added to all your conversations across all models.
              </div>
            </div>
            <div className="settings-info-row">
              <svg width={20} height={20} fill="var(--warning)" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: "2px" }}>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
              </svg>
              <div style={{ fontSize: "0.875rem", color: "var(--text-muted)", lineHeight: "1.5" }}>
                <strong style={{ color: "var(--text-secondary)" }}>Priority:</strong> Model-specific prompts (set in model settings) will override this global prompt.
              </div>
            </div>
          </div>
          <div className="form-group">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
              <label className="form-label" style={{ marginBottom: 0 }}>Your Global Prompt</label>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ padding: "0.25rem 0.75rem", fontSize: "0.75rem", borderRadius: "0.5rem" }}
                onClick={() => setShowPromptLibrary(true)}
              >
                Prompt Library
              </button>
            </div>
            <textarea
              className="input"
              value={globalPrompt}
              onChange={e => setGlobalPrompt(e.target.value)}
              placeholder="Example: You are a helpful assistant. Always be concise and friendly..."
              rows={6}
              style={{ resize: "vertical", fontFamily: "inherit", fontSize: "0.875rem" }}
            />
            <div style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginTop: "0.5rem" }}>
              {globalPrompt.length} characters
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
            <button
              className="btn btn-secondary"
              onClick={() => setGlobalPrompt("")}
              disabled={!globalPrompt || savingGlobalPrompt}
            >
              Clear
            </button>
            <button
              className="btn btn-primary"
              onClick={() => saveGlobalPrompt(globalPrompt)}
              disabled={savingGlobalPrompt}
            >
              {savingGlobalPrompt ? "Saving..." : "Save Global Prompt"}
            </button>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-header">
          <svg
            className="card-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            {/* top disk */}
            <ellipse cx="12" cy="6.5" rx="7" ry="3" />
            {/* body */}
            <path d="M5 6.5v10c0 1.7 3.1 3 7 3s7-1.3 7-3v-10" />
            {/* middle rim */}
            <path d="M5 11.5c0 1.7 3.1 3 7 3s7-1.3 7-3" />
            {/* bottom rim */}
            <path d="M5 16.5c0 1.7 3.1 3 7 3s7-1.3 7-3" />
          </svg>
          <h2 style={{ flex: 1 }}>Context Memory</h2>
        </div>
        <div style={{ marginTop: "1rem" }}>
          <div className="context-memory-grid">
            {[
              { val: 20, label: "Low" },
              { val: 40, label: "Fast" },
              { val: 60, label: "Balanced" },
              { val: 80, label: "High" },
              { val: 100, label: "Max" }
            ].map((opt) => {
              const isActive = contextLimit === opt.val;
              return (
                <button
                  key={opt.val}
                  className={`context-btn ${isActive ? "active" : ""}`}
                  onClick={() => {
                    saveContextLimit(opt.val);
                    trackEvent("settings_context_limit_change", { value: opt.val });
                    // showToast(`Memory limit set to ${opt.val}%`, "success");
                  }}
                >
                  <span style={{ fontWeight: '600' }}>
                    {opt.val}%
                  </span>
                  <span style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                    {opt.label}
                  </span>
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: "0.875rem", color: "var(--text-muted)", lineHeight: "1.5" }}>
            This controls how much the AI remembers. Lower settings are faster and usually cost less, but may forget older details. Higher settings remember more, but can be slower and cost more.
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <svg className="card-icon" viewBox="0 0 100 100">
            <g transform="translate(50 50) scale(0.85) rotate(-45) translate(-50 -50)">
              <path fill="currentColor" d="M 5 15 L 21 15 L 26 20 L 26 80 L 21 85 L 5 85 Z" />
              <path fill="currentColor" d="M 95 15 L 79 15 L 74 20 L 74 80 L 79 85 L 95 85 Z" />
              <path fill="currentColor" d="M 32 20 h 8 v 60 h -8 z M 46 20 h 8 v 60 h -8 z M 60 20 h 8 v 60 h -8 z" />
            </g>
          </svg>
          <h2 style={{ flex: 1 }}>
            Thread Mode{" "}
            <span style={{ color: "var(--text-muted)", fontWeight: 600 }}>
              (Personal)
            </span>
          </h2>
        </div>
        <div style={{ marginTop: "1rem" }}>
          <div className="thread-mode-row" style={{ marginBottom: "1.25rem", flexWrap: "wrap" }}>
            <div className="thread-mode-row-text settings-row-text">
              <span className="thread-mode-row-title">Default Model</span>
              <span className="thread-mode-row-desc">
                Used when you type <span className="inline-code-subtle">/chat</span> without selecting a model. Also applies to Direct Messages.
              </span>
            </div>
            <div className="model-selector-wrapper settings-dropdown-container">
              <input
                className="model-selector-input"
                placeholder="Search models..."
                value={modelSelectorOpen
                  ? modelSelectorSearch
                  : defaultBehavior === "last"
                    ? "🕒 Last Used (Dynamic)"
                    : (activeDefaultModel?.displayName || "Pick a model")
                }
                onChange={e => { setModelSelectorSearch(e.target.value); setModelSelectorOpen(true); }}
                onClick={() => { setModelSelectorOpen(true); setModelSelectorSearch(""); }}
                onBlur={() => setTimeout(() => { setModelSelectorOpen(false); setModelSelectorSearch(""); }, 200)}
              />
              <div style={{ position: "absolute", right: "0.75rem", top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "var(--text-muted)", fontSize: "0.75rem" }}>▼</div>
              {modelSelectorOpen && (
                <div className="model-selector-dropdown" style={{ width: "max-content", minWidth: "100%", maxWidth: "min(380px, 90vw)" }}>
                  <div
                    className={`model-selector-option ${defaultBehavior === "last" ? "focused" : ""}`}
                    onMouseDown={() => {
                      handleDefaultDropdownChange({ target: { value: "last" } });
                      setModelSelectorOpen(false);
                      setModelSelectorSearch("");
                    }}
                  >
                    🕒 Last Used (Dynamic)
                  </div>
                  {models?.flatMap(group =>
                    (group.models || [])
                      .filter(m => m.enabled && (
                        !modelSelectorSearch ||
                        m.displayName.toLowerCase().includes(modelSelectorSearch.toLowerCase()) ||
                        group.provider.toLowerCase().includes(modelSelectorSearch.toLowerCase())
                      ))
                      .map(m => (
                        <div
                          key={m.id}
                          className={`model-selector-option ${activeDefaultModel?.id === m.id ? "focused" : ""}`}
                          onMouseDown={() => {
                            handleDefaultDropdownChange({ target: { value: m.id } });
                            setModelSelectorOpen(false);
                            setModelSelectorSearch("");
                          }}
                        >
                          <span className="model-selector-badge">{group.provider === "custom" ? "custom" : group.provider}</span>
                          {m.displayName}
                        </div>
                      ))
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="thread-mode-row">
            <div className="thread-mode-row-text">
              <span className="thread-mode-row-title">
                Allow others to join my <span className="inline-code-subtle">/chat</span> threads
              </span>
              <span className="thread-mode-row-desc">
                If enabled, anyone in the server can join and send prompts in conversations you started with the bot. Otherwise, only you can chat with your AI (others are ignored).
              </span>
            </div>
            <label className="switch-inline">
              <input
                type="checkbox"
                checked={!!allowOthers}
                onChange={e => saveAllowOthersPreference(e.target.checked)}
              />
              <span className="slider"></span>
            </label>
          </div>
          <p className="thread-mode-note">
            Note: In <span className="inline-code-subtle">/chat</span> threads, every Discord user uses their own OkeyBot account, even with the same bot in the same server. Keys and credits are tied to your account and never shared server‑wide; this setting only controls whether others can send requests that use your account in conversations you started.
          </p>

          <hr className="settings-divider" style={{ margin: "1.5rem 0", borderColor: "var(--border-color)", opacity: 0.5 }} />

          <div className="thread-mode-row" style={{ alignItems: "flex-start" }}>
            <div className="thread-mode-row-text">
              <span className="thread-mode-row-title" style={{ display: "flex", alignItems: "center" }}>
                Memory
                <span className="beta-badge" style={{ fontSize: "0.6rem", padding: "0.15rem 0.4rem", borderRadius: "10px", background: "var(--accent)", color: "#fff", marginLeft: "0.5rem", verticalAlign: "middle" }}>BETA</span>
              </span>
              <span className="thread-mode-row-desc">
                Allow the AI to automatically learn and remember details about you across your private threads and DMs.
                This uses a private Discord channel as secure storage.
              </span>
              <span className="thread-mode-row-desc" style={{ marginTop: "0.25rem", fontSize: "0.8rem", opacity: 0.7 }}>
                30 memories. As they accumulate, it curates them. Improves and gets sharper over time.
              </span>
            </div>
            {memorySettings && (
              <label className="switch-inline" style={{ marginTop: "0.25rem" }}>
                <input
                  type="checkbox"
                  checked={memorySettings.enabled}
                  onChange={(e) => saveMemorySettings({ ...memorySettings, enabled: e.target.checked })}
                  disabled={savingMemory}
                />
                <span className="slider"></span>
              </label>
            )}
          </div>

          {memorySettings?.enabled && (
            <div style={{ marginTop: "1rem", padding: "1.25rem", background: "rgba(0,0,0,0.15)", borderRadius: "8px", border: "1px solid var(--border-color)" }}>
              {memorySettings.warning && (
                <div style={{ background: "rgba(245, 158, 11, 0.1)", border: "1px solid rgba(245, 158, 11, 0.35)", padding: "1rem", borderRadius: "8px", marginBottom: "1.25rem" }}>
                  <div style={{ color: "#fbbf24", fontWeight: 600, marginBottom: "0.35rem" }}>Memory paused</div>
                  <div style={{ color: "#d1d5db", fontSize: "0.85rem", lineHeight: 1.5 }}>{memorySettings.warning}</div>
                </div>
              )}
              <div className="thread-mode-row" style={{ marginBottom: "1.25rem", flexWrap: "wrap" }}>
                <div className="thread-mode-row-text settings-row-text">
                  <span className="thread-mode-row-title">Home Server</span>
                  <span className="thread-mode-row-desc">
                    The server where your private memory channel will be created.
                  </span>
                </div>
                <div className="model-selector-wrapper settings-dropdown-container">
                  <input
                    className="model-selector-input"
                    placeholder={savingMemory || creatingChannel ? "Loading..." : "Search servers..."}
                    value={serverSelectorOpen
                      ? serverSelectorSearch
                      : (activeGuild?.name || "Select a server...")
                    }
                    onChange={e => { setServerSelectorSearch(e.target.value); setServerSelectorOpen(true); }}
                    onClick={() => { if (!savingMemory && !creatingChannel) { setServerSelectorOpen(true); setServerSelectorSearch(""); } }}
                    onBlur={() => setTimeout(() => { setServerSelectorOpen(false); setServerSelectorSearch(""); }, 200)}
                    disabled={savingMemory || creatingChannel}
                    style={{ opacity: (savingMemory || creatingChannel) ? 0.7 : 1 }}
                  />
                  <div style={{ position: "absolute", right: "0.75rem", top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "var(--text-muted)", fontSize: "0.75rem" }}>▼</div>
                  {serverSelectorOpen && (
                    <div className="model-selector-dropdown" style={{ width: "100%", maxHeight: "300px" }}>
                      {(guilds || [])
                        .filter(g => !serverSelectorSearch || g.name.toLowerCase().includes(serverSelectorSearch.toLowerCase()))
                        .map(g => (
                          <div
                            key={g.id}
                            className={`model-selector-option ${activeGuild?.id === g.id ? "focused" : ""}`}
                            onMouseDown={() => {
                              saveMemorySettings({ ...memorySettings, guild_id: g.id });
                              setServerSelectorOpen(false);
                              setServerSelectorSearch("");
                            }}
                          >
                            {g.name}
                          </div>
                        ))}
                      {(guilds || []).filter(g => !serverSelectorSearch || g.name.toLowerCase().includes(serverSelectorSearch.toLowerCase())).length === 0 && (
                        <div className="model-selector-option" style={{ opacity: 0.5, pointerEvents: "none" }}>
                          No servers found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="thread-mode-row" style={{ marginBottom: memorySettings.channel_id ? "1.25rem" : "0", flexWrap: "wrap", alignItems: "center" }}>
                <div className="thread-mode-row-text settings-row-text">
                  <span className="thread-mode-row-title">Memory Channel</span>
                  <span className="thread-mode-row-desc">
                    Your secure, private storage channel in Discord.
                  </span>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                  {!memorySettings.channel_id ? (
                    needsPermissionUpdate ? (
                      <div className="permission-warning" style={{ background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)", padding: "1rem", borderRadius: "8px", maxWidth: "400px" }}>
                        <div style={{ color: "#fca5a5", fontWeight: "600", marginBottom: "0.5rem" }}>
                          {needsPermissionUpdate === "invite" ? "Bot Not In Server" : "Missing Permissions"}
                        </div>
                        <div style={{ color: "#d1d5db", fontSize: "0.85rem", marginBottom: "1rem", lineHeight: "1.5" }}>
                          {needsPermissionUpdate === "invite"
                            ? "OkeyBot must be invited to this server to set up your memory channel."
                            : "The bot does not have the Manage Channels or Manage Roles permissions in this server, which are required to create your private memory channel securely. Please update the bot's permissions to continue."}
                        </div>
                        <a
                          href={`${BOT_INVITE_URL}&guild_id=${memorySettings.guild_id}&disable_guild_select=true`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary"
                          style={{ display: "inline-flex", textDecoration: "none", justifyContent: "center", width: "100%" }}
                          onClick={() => setNeedsPermissionUpdate(false)}
                        >
                          {needsPermissionUpdate === "invite" ? "Invite OkeyBot to Server" : "Update Permissions in Discord"}
                        </a>
                      </div>
                    ) : (
                      <button
                        className="btn btn-primary"
                        onClick={handleCreateMemoryChannel}
                        disabled={creatingChannel || !memorySettings.guild_id}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {creatingChannel ? "Setting up..." : "Setup #okey-memory"}
                      </button>
                    )
                  ) : (
                    <>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.4rem 0.75rem", background: "rgba(255,255,255,0.05)", borderRadius: "6px", border: "1px solid var(--border-color)", fontSize: "0.875rem" }}>
                        <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "var(--success)" }}></div>
                        <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>#okey-memory</span>
                      </div>
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          if (confirm("Are you sure you want to unlink this channel? The bot will stop remembering things here.")) {
                            saveMemorySettings({ ...memorySettings, channel_id: "" });
                          }
                        }}
                        style={{ padding: "0.4rem 0.75rem", fontSize: "0.875rem" }}
                      >
                        Unlink
                      </button>
                    </>
                  )}
                </div>
              </div>

              {memorySettings.channel_id && (
                <div className="thread-mode-row" style={{ flexWrap: "wrap", alignItems: "flex-start" }}>
                  <div className="thread-mode-row-text settings-row-text">
                    <span className="thread-mode-row-title">Extraction Model</span>
                    <span className="thread-mode-row-desc">
                      Used silently in the background. We recommend a fast, cheap model.
                    </span>
                  </div>
                  <div className="model-selector-wrapper settings-dropdown-container">
                    <input
                      className="model-selector-input"
                      placeholder={savingMemory ? "Loading..." : "Search models..."}
                      value={extractorInputValue}
                      onChange={e => { setExtractorSelectorSearch(e.target.value); setExtractorSelectorOpen(true); }}
                      onClick={() => { if (!savingMemory) { setExtractorSelectorOpen(true); setExtractorSelectorSearch(""); } }}
                      onBlur={() => setTimeout(() => { setExtractorSelectorOpen(false); setExtractorSelectorSearch(""); }, 200)}
                      disabled={savingMemory}
                      style={{ opacity: savingMemory ? 0.7 : 1 }}
                    />
                    <div style={{ position: "absolute", right: "0.75rem", top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "var(--text-muted)", fontSize: "0.75rem" }}>▼</div>
                    {extractorSelectorOpen && (
                      <div className="model-selector-dropdown" style={{ width: "max-content", minWidth: "100%", maxWidth: "min(380px, 90vw)" }}>
                        <div
                          className={`model-selector-option ${memorySettings.extractor_auto ? "focused" : ""}`}
                          onMouseDown={() => {
                            saveMemorySettings({ ...memorySettings, extractor_provider: "", extractor_model: "", extractor_auto: true });
                            setExtractorSelectorOpen(false);
                            setExtractorSelectorSearch("");
                          }}
                        >
                          <span className="model-selector-badge">auto</span>
                          {autoExtractorLabel}
                        </div>
                        {extractionModels
                          .filter(m => (
                            !extractorSelectorSearch ||
                            (m.displayName || m.name || "").toLowerCase().includes(extractorSelectorSearch.toLowerCase()) ||
                            m.provider.toLowerCase().includes(extractorSelectorSearch.toLowerCase())
                          ))
                          .map(m => (
                            <div
                              key={`${m.provider}|${m.name}`}
                              className={`model-selector-option ${!memorySettings.extractor_auto && activeExtractorModel?.provider === m.provider && activeExtractorModel?.name === m.name ? "focused" : ""}`}
                              onMouseDown={() => {
                                saveMemorySettings({ ...memorySettings, extractor_provider: m.provider, extractor_model: m.name, extractor_auto: false });
                                setExtractorSelectorOpen(false);
                                setExtractorSelectorSearch("");
                              }}
                            >
                              <span className="model-selector-badge">{m.provider === "custom" ? "custom" : m.provider}</span>
                              {m.displayName || m.name}
                            </div>
                          ))
                        }
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
      <div className="card">
        <div className="card-header">
          <svg className="card-icon" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10h5v-2h-5c-4.34 0-8-3.66-8-8s3.66-8 8-8 8 3.66 8 8v1.43c0 .79-.71 1.57-1.5 1.57s-1.5-.78-1.5-1.57V12c0-2.76-2.24-5-5-5s-5 2.24-5 5 2.24 5 5 5c1.38 0 2.64-.56 3.54-1.47.65.89 1.77 1.47 2.96 1.47 1.97 0 3.5-1.6 3.5-3.57V12c0-5.52-4.48-10-10-10zm0 13c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z" />
          </svg>
          <h2 style={{ flex: 1 }}>
            Mention Mode{" "}
            <span style={{ color: "var(--text-muted)", fontWeight: 600 }}>
              (Server)
            </span>
          </h2>
        </div>
        <div style={{ marginTop: "1rem" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: "0.875rem", color: "#9ca3af", lineHeight: "1.5" }}>
                Choose which model replies when someone @mentions the bot in a server. These replies use the server owner's plan limits and balance/costs.
              </div>
            </div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button className="btn btn-primary" onClick={openAddMapping}>
                Add
              </button>
            </div>
          </div>

          {loadingGuilds ? (
            <div className="spinner" style={{ margin: "1.5rem auto" }} />
          ) : assignedCount === 0 ? (
            <div style={{ marginTop: "0.5rem", fontSize: "0.875rem", color: "var(--text-muted)" }}>
              Not set in any server yet.
            </div>
          ) : (
            <>
              <div className="mention-mapping-list" style={{ marginTop: "0.75rem" }}>
                {assignedGuilds
                  .slice()
                  .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                  .slice(0, 8)
                  .map((g) => (
                    <MentionMappingRow
                      key={g.id}
                      guild={g}
                      displayName={assignments[g.id]?.display_name}
                      onRemove={async () => {
                        try {
                          const res = await fetch(`${API_BASE}/api/servers/assign/${g.id}`, {
                            method: "DELETE",
                            credentials: "include",
                          });
                          const data = await res.json().catch(() => ({}));
                          if (!res.ok || data?.error) throw new Error(data?.error || "Failed to remove");
                          setAssignments((prev) => {
                            const next = { ...(prev || {}) };
                            delete next[g.id];
                            return next;
                          });
                          showToast("Mapping removed", "info");
                        } catch (e) {
                          showToast(e.message || "Failed to remove", "error");
                        }
                      }}
                    />
                  ))}
              </div>
              {assignedCount > 8 && (
                <div style={{ paddingTop: "0.5rem", fontSize: "0.8125rem", color: "var(--text-muted)", fontWeight: 800 }}>
                  +{assignedCount - 8} more
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <svg className="card-icon" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
          </svg>
          <h2 style={{ flex: 1 }}>Billing & Subscription</h2>
        </div>
        <div style={{ marginTop: "1rem" }}>
          <div style={{ marginBottom: "1.5rem" }}>
            {billingStatusLoading ? (
              <div className="spinner"></div>
            ) : billingStatus?.is_premium ? (
              <>
                <div className="billing-status-active">
                  <svg width={24} height={24} fill="var(--accent)" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  <div className="billing-status-text">
                    <div className="billing-status-title">Premium Subscription Active</div>
                    {billingStatus?.current_period_end && (
                      <div className="billing-status-desc">
                        Current period ends on {new Date(billingStatus.current_period_end).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                  <button
                    className="btn btn-secondary"
                    onClick={openBillingPortal}
                    disabled={billingLoading}
                    title="Manage payment method / subscription"
                  >
                    {billingLoading ? "Opening..." : "Manage Billing"}
                  </button>
                </div>
                <div style={{ fontSize: "0.875rem", color: "#9ca3af", marginTop: "0.75rem" }}>
                  Use "Manage Billing" to update payment method or cancel your subscription.
                </div>
              </>
            ) : (
              <>
                <div style={{
                  marginBottom: "1rem",
                  padding: "1rem",
                  border: "1px solid var(--border-color)",
                  borderRadius: "8px"
                }}>
                  <div style={{ fontWeight: "600", color: "var(--text-primary)", marginBottom: "0.5rem" }}>
                    Free Plan
                  </div>
                  <div style={{ fontSize: "0.875rem", color: "var(--text-secondary)" }}>
                    • 20 messages/day<br />
                    • 3000 one-time credits to try built-in models<br />
                    • All supported providers<br />
                    • Works with your own API keys<br />
                    • Custom model presets<br />
                    • 0% markup on your own API usage<br />
                    • Usage tracking
                  </div>
                </div>
                <div style={{
                  marginBottom: "1rem",
                  padding: "1rem",
                  background: "rgba(20, 184, 166, 0.08)",
                  border: "1px solid rgba(20, 184, 166, 0.25)",
                  borderRadius: "8px"
                }}>
                  <div style={{ fontWeight: "600", color: "var(--accent)", marginBottom: "0.5rem" }}>
                    Upgrade to Premium
                  </div>
                  <div style={{ fontSize: "0.875rem", color: "var(--text-secondary)" }}>
                    • Unlimited messages<br />
                    • Code/text attachments support<br />
                    • 15000 monthly credits for built-in models<br /><br />
                    <span style={{ color: "var(--text-muted)" }}>Everything else stays the same: all supported providers, your own API keys, custom model presets, 0% markup on your own API usage, usage tracking.</span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => startCheckout("premium_monthly")}
                    disabled={billingLoading}
                    title="$3.99/month"
                    style={{ flex: "1", minWidth: "160px" }}
                  >
                    {billingLoading ? "Loading..." : "Monthly ($3.99/mo)"}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => startCheckout("premium_yearly")}
                    disabled={billingLoading}
                    title="$35.88/year (Save 25%)"
                    style={{ flex: "1", minWidth: "160px", position: "relative" }}
                  >
                    {billingLoading ? "Loading..." : "Yearly ($2.99/mo)"}
                    <span
                      style={{ position: "absolute", top: "-8px", right: "-8px", background: "var(--success)", color: "white", fontSize: "0.625rem", padding: "0.125rem 0.375rem", borderRadius: "9999px", fontWeight: "700" }}
                    >
                      SAVE 25%
                    </span>
                  </button>
                </div>
                <div style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginTop: "0.75rem" }}>
                  Note: You can try OkeyBot with built-in models before adding any API keys. Both plans let you connect your own keys, with the same Free vs Premium message limits.<br />
                  Our fees cover the Discord bot, usage tracking, and the credits we include. When you use your own keys, provider costs (OpenAI, Anthropic, etc.) are billed separately by them.<br />
                  Free plan one-time credits may change.<br />
                  Billed monthly or yearly. Cancel anytime from the billing portal.
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="card danger-zone-card">
        <div className="card-header">
          <svg className="card-icon danger-icon" fill="currentColor" viewBox="0 0 24 24">
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
          </svg>
          <h2 style={{ flex: 1 }} className="danger-title">Danger Zone</h2>
        </div>
        <div style={{ marginTop: "1rem" }}>
          <div className="danger-warning">
            <div style={{
              display: "flex",
              alignItems: "start",
              gap: "0.75rem"
            }}>
              <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: "2px" }} className="danger-icon">
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
              </svg>
              <div style={{ fontSize: "0.875rem", color: "var(--text-muted)", lineHeight: "1.5" }}>
                <strong className="danger-text">Warning:</strong> Deleting your account is permanent and cannot be undone. All your data, including API keys, models, settings, and usage history will be permanently deleted.
              </div>
            </div>
          </div>

          {!showDeleteConfirm ? (
            <button
              className="btn btn-danger-subtle"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete My Account
            </button>
          ) : (
            <div style={{ animation: "slideDown 0.2s ease-out" }}>
              <div className="form-group">
                <label className="form-label danger-text">
                  Type <strong>DELETE</strong> to confirm account deletion
                </label>
                <input
                  className="input danger-input"
                  type="text"
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE here"
                  autoFocus
                />
                <div style={{ fontSize: "0.875rem", color: "#9ca3af", marginTop: "0.5rem" }}>
                  This will permanently delete your account and all associated data.
                </div>
              </div>
              <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText("");
                  }}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirmText !== "DELETE" || deleting}
                >
                  {deleting ? "Deleting..." : "Permanently Delete Account"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {showPromptLibrary && (
        <PromptLibraryModal
          onClose={() => setShowPromptLibrary(false)}
          onInsert={handleInsertPrompt}
        />
      )}

      {showAddMapping && (
        <AddServerMentionMappingModal
          onClose={() => setShowAddMapping(false)}
          guilds={guilds}
          assignments={assignments}
          setAssignments={setAssignments}
          refreshGuilds={fetchGuildsAndAssignments}
        />
      )}
    </>
  );
}
